import { CopyElement, Chip, proxyNumber, ROUTER_ID_BASE } from "@ui";
import cn from "classnames";
import style from "./TableRow.module.scss";
import { IProxy } from "@interfaces/admin/proxy";
import { VodafoneSvg, LifecellSvg, KyivstarSvg, ArrowSvg } from "./svg";
import { PhoneNumberCell, PaySumCell, SellingCell, PayDateCell, AutoPayCell, TariffCell, DayQuantityCell, SettingsCell, OperatorSelectCell } from "./components";
import { Children, isValidElement, ReactNode, useState } from "react";
import { useAppSelector } from "store/hooks";
import { DeleteModemButton } from "./components/SettingsCell/DeleteModemButton";

// Короткий номер — общий для всей панели и кабинета помощник, см. @ui/proxyNumber.
// Нодовый номер может совпасть с легаси-номером → в списке два «№7». Бейдж «нода»
// различает их (router_id ≥ базы = модем на нашей ферме). Легаси идут без бейджа.
const idLabel = (rid: number) => (
    <p className="text-[#1b1b1d] whitespace-nowrap text-[13px] font-medium">
        №{proxyNumber({ router_id: rid })}
        {rid >= ROUTER_ID_BASE && (
            <span className="ml-1 inline-block rounded px-1 py-[1px] text-[10px] font-semibold bg-[#e6f4ea] text-[#1b7a3d] align-middle">
                нода
            </span>
        )}
    </p>
);

export function TableRow({
    data
}: {
    data: IProxy
}) {
    const [showChildren, setShowChildren] = useState<boolean>(true);

    const rowsIsError = useAppSelector(state => state.myProxiesAdminPage.rowsIsError);

    return (
        <div data-proxy-id={data.id} className={cn(style.row, rowsIsError[data.id]?.length > 0 && "bg-[#ffd8d833]")}>
            <Fields>
            <div className={style.cell}>
                <CopyElement
                    element={idLabel(data.router_id)}
                    copyText={proxyNumber(data)}
                />
            </div>
            <SellingCell proxy={data} selling={data.selling} />
            <div className={cn(style.cell, "w-[52px] flex items-center justify-center")} >
                {
                    data.pay_date && isDateValid(new Date(data.pay_date || ""))
                        ? <div className={style.greenCircle}></div>
                        : <div className={style.redCircle}></div>
                }
            </div>
            <OperatorSelectCell data={data} showChildren={showChildren} setShowChildren={setShowChildren}/>
            <PhoneNumberCell proxy={data} dataPhone={data.phone_number} />
            <PayDateCell proxy_id={data.id} pay_date={data.pay_date} />
            <AutoPayCell proxy_id={data.id} auto_pay={data.auto_pay} />
            <div className={style.cell}>
                <Chip
                    text={data.status}
                    color={data.status === "Online" ? "green" : "red"}
                />
            </div>
            <div className={cn(style.cell, "text-[#1B1B1D] text-[13px] font-medium truncate")}>
                {data.modem}
            </div>
            <PaySumCell proxy_id={data.id} paySum={data.pay_sum} />
            <TariffCell proxy={data} currentTariff={data.tariff} />
            <DayQuantityCell proxy_id={data.id} day_quantity={data.day_quantity} />
            <div className={style.cell}>
                <CopyElement
                    element={<p className="text-[#1b1b1d] text-[13px] font-medium">id: {data.user_id}</p>}
                    copyText={String(data.user_id)}
                />
            </div>
            <SettingsCell data={data} />
            </Fields>
            <div className={cn(style.children, showChildren && style.children_show)}>
                {
                    data.children && data.children.map((child) => (
                        <div key={child.id} data-proxy-id={child.id} className={cn(style.row, style.childRow)}>
                            <Fields>
                            <div className={style.cell}>
                                <CopyElement
                                    element={idLabel(child.router_id)}
                                    copyText={proxyNumber(child)}
                                />
                            </div>
                            <SellingCell proxy={child} selling={child.selling} />
                            <div className={cn(style.cell, "w-[52px] flex items-center justify-center")}></div>
                            <OperatorSelectCell data={child} showChildren={showChildren} setShowChildren={setShowChildren} isParent={false}/>
                            <PhoneNumberCell proxy={child} dataPhone={child.phone_number} />
                            <div className={style.cell}></div>
                            <div className={style.cell}></div>
                            <div className={style.cell}>
                                <Chip
                                    text={child.status}
                                    color={child.status === "Online" ? "green" : "red"}
                                />
                            </div>
                            <div className={cn(style.cell, "text-[#1B1B1D] text-[13px] font-medium truncate")}>
                                {child.modem}
                            </div>
                            <div className={style.cell}></div>
                            <TariffCell proxy={child} currentTariff={child.tariff} />
                            <div className={style.cell}></div>
                            <div className={style.cell}>
                                <CopyElement
                                    element={<p className="text-[#1b1b1d] text-[13px] font-medium">id: {child.user_id}</p>}
                                    copyText={String(child.user_id)}
                                />
                            </div>
                            <div className={style.cell}><DeleteModemButton data={child} /></div>
                            </Fields>
                        </div>
                    ))
                }
            </div>
        </div>
    )
}
function isDateValid(inputDate: Date) {
    const currentDate = new Date();

    currentDate.setHours(0, 0, 0, 0);
    inputDate.setHours(0, 0, 0, 0);

    const diffInDays = (inputDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24);

    return diffInDays > 2;
}

const fieldLabels = ["Порт", "Продаж", "Термін", "Оператор", "Номер телефону", "Дата поповнення", "Автопрод.", "Статус", "Модем", "Сума поповнення", "Тариф", "Період", "ID клієнта", "Дії"];
function Fields({ children }: { children: ReactNode }) {
    return <>{Children.toArray(children).map((child, index) => {
        const empty = isValidElement(child) && child.type === "div" && !child.props.children;
        return <div key={index} data-field={index} className={cn(style.field, empty && style.emptyField,
            [3, 4, 5, 8, 10, 12, 13].includes(index) && style.wideField)}>
            <span className={style.fieldLabel}>{fieldLabels[index]}</span>{child}
        </div>;
    })}</>;
}
